// Re-export all public types so existing imports from 'NewWcnTable' continue to work.
export type {
    KeyValuePair,
    DataTableColumn,
    DataTablePagination,
    DataTableSorting,
    DataTableSelection,
    DataTableExpansion,
    WcnTableProps
} from './tableTypes';
export { TableQueryEvent } from './tableTypes';
export type { TableQuery } from './tableTypes';

import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    IconButton,
    Table,
    TableContainer,
    TablePagination,
    Toolbar,
    Typography,
    Theme,
    LinearProgress
} from '@mui/material';

const BiChevronDown = () => (
    <i
        className="bi bi-chevron-down"
        style={{ fontSize: 16, position: 'absolute', right: 0, pointerEvents: 'none', lineHeight: 1 }}
    />
);

interface PaginationActionsProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

const WcnPaginationActions: React.FC<PaginationActionsProps> = ({ count, page, rowsPerPage, onPageChange }) => (
    <>
        <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} size="small">
            <i className="bi bi-chevron-left" style={{ fontSize: 16, lineHeight: 1 }} />
        </IconButton>
        <IconButton
            onClick={(e) => onPageChange(e, page + 1)}
            disabled={page >= Math.ceil(count / rowsPerPage) - 1}
            size="small"
        >
            <i className="bi bi-chevron-right" style={{ fontSize: 16, lineHeight: 1 }} />
        </IconButton>
    </>
);
import { useTranslation } from 'react-i18next';
import { useSettingsStore } from '@core/store';
import { DataTableExpansion, TableQueryEvent, WcnTableProps } from './tableTypes';
import { useTableSort } from '@core/hooks/useTableSort';
import WcnTableHead from './components/WcnTableHead';
import WcnTableBody from './components/WcnTableBody';
import WcnMobileCards from './components/WcnMobileCards';

const NewWcnTable = <T,>({
    title,
    columns,
    data = [],
    loading = false,
    pagination,
    sorting,
    customSort,
    selection,
    expansion,
    noDataMessage,
    onRowClick,
    getRowProps,
    onQueryChange,
    size = 'small',
    renderEmpty,
    mobileView = 'auto'
}: Readonly<WcnTableProps<T>>) => {
    const { t } = useTranslation();
    const { isMobile } = useSettingsStore();

    const shouldAutoCard =
        mobileView === 'auto' && (!!selection || columns.some((c) => c.multiLine) || columns.some((c) => c.isAction));
    const showCardView = isMobile && (mobileView === 'card' || shouldAutoCard);

    // ── Pagination state ──────────────────────────────────────────────────────
    // Internal state is the fallback when no external controlled values are provided.
    const [page, setPage] = useState(pagination?.page ?? 0);
    const [rowsPerPage, setRowsPerPage] = useState(pagination?.rowsPerPage ?? 5);

    // Resolved values — external (controlled) wins over internal
    const isControlledPage = !!(onQueryChange || pagination?.onPageChange);
    const isControlledPageSize = !!(onQueryChange || pagination?.onRowsPerPageChange);
    const activePage = isControlledPage ? (pagination?.page ?? page) : page;
    const activeRowsPerPage = isControlledPageSize ? (pagination?.rowsPerPage ?? rowsPerPage) : rowsPerPage;

    // ── Expansion state ───────────────────────────────────────────────────────
    const [internalExpandedRows, setInternalExpandedRows] = useState<(string | number)[]>([]);
    const expandedRows = expansion?.expandedRows ?? internalExpandedRows;
    const handleExpandToggle =
        expansion?.onExpandToggle ??
        ((rowKey: string | number) => {
            setInternalExpandedRows((prev) =>
                prev.includes(rowKey) ? prev.filter((k) => k !== rowKey) : [...prev, rowKey]
            );
        });

    // ── Sort ──────────────────────────────────────────────────────────────────
    // Passes current page/pageSize so handleSortClick can include them in TableQuery.
    // Also passes onQueryChange so sort events go through the unified callback.
    const { sortedData, staleData, activeSortBy, activeSortDirection, handleSortClick } = useTableSort({
        columns,
        data,
        sorting,
        customSort,
        page: activePage,
        pageSize: activeRowsPerPage,
        onQueryChange
    });

    // ── Anti-flash display data ───────────────────────────────────────────────
    // While loading=true and the new data hasn't arrived yet (sortedData is empty),
    // show the previous result (staleData) so the table never flashes blank.
    const displayData = loading && sortedData.length === 0 ? staleData : sortedData;

    // ── Pagination slice (client-side pagination only) ────────────────────────
    // External onPageChange = server handles slicing — don't slice here.
    // onQueryChange = server handles slicing — don't slice here.
    const paginatedData = useMemo(() => {
        if (!pagination) return displayData;
        if (pagination.onPageChange || onQueryChange) return displayData;
        return displayData.slice(activePage * activeRowsPerPage, (activePage + 1) * activeRowsPerPage);
    }, [displayData, pagination?.onPageChange, onQueryChange, activePage, activeRowsPerPage]);

    // ── Derived layout values ─────────────────────────────────────────────────
    const hasExpandableRows = useMemo(() => {
        if (!expansion || !data.length) return false;
        if (!expansion.showExpandIconCondition) return true;
        return data.some((row) => expansion.showExpandIconCondition!(row));
    }, [expansion, data]);

    const colSpan = useMemo(() => {
        let n = columns.length;
        if (selection) n += 1;
        if (hasExpandableRows) n += 1;
        return n;
    }, [columns.length, selection, hasExpandableRows]);

    // ── Page change handler ───────────────────────────────────────────────────
    const handlePageChange = useCallback(
        (_event: unknown, newPage: number) => {
            if (onQueryChange) {
                // Unified path: emit full query so the page builds its API call
                onQueryChange(
                    {
                        sortBy: activeSortBy,
                        sortDirection: activeSortDirection,
                        page: newPage,
                        pageSize: activeRowsPerPage
                    },
                    TableQueryEvent.PAGE
                );
            } else if (pagination?.onPageChange) {
                pagination.onPageChange(_event, newPage);
            } else {
                setPage(newPage);
            }
        },
        [onQueryChange, pagination?.onPageChange, activeSortBy, activeSortDirection, activeRowsPerPage]
    );

    // ── Page size change handler ──────────────────────────────────────────────
    const handleRowsPerPageChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newSize = Number.parseInt(e.target.value, 10);
            if (onQueryChange) {
                onQueryChange(
                    {
                        sortBy: activeSortBy,
                        sortDirection: activeSortDirection,
                        page: 0, // always reset to first page on size change
                        pageSize: newSize
                    },
                    TableQueryEvent.PAGE_SIZE
                );
            } else if (pagination?.onRowsPerPageChange) {
                pagination.onRowsPerPageChange(e);
            } else {
                setRowsPerPage(newSize);
                setPage(0);
            }
        },
        [onQueryChange, pagination?.onRowsPerPageChange, activeSortBy, activeSortDirection]
    );

    // ── Row interaction handlers ──────────────────────────────────────────────
    const handleRowSelection = useCallback(
        (row: T, event?: React.MouseEvent) => {
            if (!selection) return;
            event?.stopPropagation();
            const rowKey = selection.getItemKey(row);
            const isSelected = selection.selectedItems.some((item) => selection.getItemKey(item) === rowKey);
            if (selection.selectionMode === 'single') {
                selection.onSelectionChange(isSelected ? [] : [row]);
            } else if (isSelected) {
                selection.onSelectionChange(
                    selection.selectedItems.filter((item) => selection.getItemKey(item) !== rowKey)
                );
            } else {
                selection.onSelectionChange([...selection.selectedItems, row]);
            }
        },
        [selection]
    );

    const handleRowExpansion = useCallback(
        (row: T, event?: React.MouseEvent) => {
            if (!expansion) return;
            event?.stopPropagation();
            handleExpandToggle(expansion.getRowKey(row));
        },
        [expansion, handleExpandToggle]
    );

    // ── Nested table renderer ─────────────────────────────────────────────────
    const renderNestedTable = useCallback(
        (props: {
            columns: any[];
            data: any[];
            size?: 'small' | 'medium';
            noDataMessage?: string;
            expansion?: DataTableExpansion<any>;
            mobileView?: 'auto' | 'scroll' | 'card';
        }) => (
            <NewWcnTable
                columns={props.columns}
                data={props.data}
                size={props.size ?? 'small'}
                noDataMessage={props.noDataMessage}
                expansion={props.expansion}
                mobileView={props.mobileView ?? mobileView}
            />
        ),
        [mobileView]
    );

    const emptyMessage = noDataMessage || t('noData');

    return (
        <Box className={`data-table__wrapper wcn-table-wrapper${selection ? ' selectable' : ''}`}>
            {title && (
                <Toolbar className="wcn-table-toolbar">
                    <Typography variant="h6" component="div">
                        {title}
                    </Typography>
                </Toolbar>
            )}

            <Box className="wcn-table-loading-container">
                {loading && <LinearProgress className="wcn-table-loading-progress" />}

                {showCardView ? (
                    <WcnMobileCards
                        paginatedData={paginatedData}
                        columns={columns}
                        selection={selection}
                        expansion={expansion}
                        expandedRows={expandedRows}
                        hasExpandableRows={hasExpandableRows}
                        mobileView={mobileView}
                        loading={loading}
                        noDataMessage={emptyMessage}
                        renderEmpty={renderEmpty}
                        onRowClick={onRowClick}
                        onRowSelection={handleRowSelection}
                        onRowExpansion={handleRowExpansion}
                        renderNestedTable={renderNestedTable}
                    />
                ) : (
                    <TableContainer className="wcn-table-container">
                        <Table
                            size={size}
                            className="wcn-table-root"
                            sx={(theme: Theme) => ({
                                backgroundColor: theme.palette.background.paper,
                                '& .MuiTableHead-root .MuiTableCell-root': {
                                    fontWeight: 400,
                                    letterSpacing: '1.4px',
                                    backgroundColor: theme.palette.action.hover
                                }
                            })}
                        >
                            <WcnTableHead
                                columns={columns}
                                hasExpandableRows={hasExpandableRows}
                                selection={selection}
                                activeSortBy={activeSortBy}
                                activeSortDirection={activeSortDirection}
                                onSortClick={handleSortClick}
                            />
                            <WcnTableBody
                                paginatedData={paginatedData}
                                columns={columns}
                                colSpan={colSpan}
                                hasExpandableRows={hasExpandableRows}
                                shouldAutoCard={shouldAutoCard}
                                loading={loading}
                                selection={selection}
                                expansion={expansion}
                                expandedRows={expandedRows}
                                noDataMessage={emptyMessage}
                                renderEmpty={renderEmpty}
                                onRowClick={onRowClick}
                                getRowProps={getRowProps}
                                onRowSelection={handleRowSelection}
                                onRowExpansion={handleRowExpansion}
                                renderNestedTable={renderNestedTable}
                            />
                        </Table>
                    </TableContainer>
                )}
            </Box>

            {pagination && (
                <Box className="wcn-table-pagination">
                    <TablePagination
                        component="div"
                        count={pagination.count}
                        page={activePage}
                        onPageChange={handlePageChange}
                        rowsPerPage={activeRowsPerPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={pagination.rowsPerPageOptions || [5, 10, 25]}
                        showFirstButton={false}
                        showLastButton={false}
                        ActionsComponent={WcnPaginationActions}
                        slotProps={{ select: { variant: 'standard', IconComponent: BiChevronDown } }}
                        labelRowsPerPage={t('rowsPerPage')}
                        labelDisplayedRows={(info) => `${info.from}-${info.to} ${t('of')} ${info.count}`}
                        sx={{ '.MuiSelect-icon': { color: 'text.secondary' } }}
                    />
                </Box>
            )}
        </Box>
    );
};

NewWcnTable.displayName = 'NewWcnTable';

export default NewWcnTable;
